import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { useFormFields, formatStatus } from 'helpers';
import {
  Row,
  Col,
  Alerts,
  ButtonAction,
  Modal,
  Container,
  PageContent,
  Pagination,
  Table,
  FiltersControls,
  Spinner,
  FiltersCustom,
} from 'components';
import {
  loadYcPeopleAppliedCreator,
  setYcPeopleAppliedSortCreator,
  setYcPeopleAppliedPageCreator,
  setYcPeopleAppliedPerPageCreator,
  setYcPeopleAppliedFiltersCreator,
  acceptYcPeopleListUserCreator,
  declineYcPeopleListUserCreator,
} from 'store/actionsCreators';
import { Margin } from 'styled-components-spacing';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RowStyled } from '../List/List.styled';

// Membership applications: pending "Join" requests plus previously "Rejected" ones (kept so
// they can be revised). Accept promotes to member; Reject (only for a pending Join) moves it
// to the revisable Rejected state.
const JOIN_STATUS = 'Join';

function Applied({
  items,
  page,
  pages,
  perPage,
  sort,
  loadUsers,
  setSort,
  loading,
  setPage,
  setPerPage,
  acceptUser,
  declineUser,
  filters,
  setFilters,
}) {
  const { t } = useTranslation();
  const [acceptConfirmModal, setAcceptConfirmModal] = useState(null);
  const [declineConfirmModal, setDeclineConfirmModal] = useState(null);
  const [filterFields, handleFilterFieldChange] = useFormFields({
    firstName: '',
    lastName: '',
    email: '',
    createdFrom: null,
    membership: null,
  });

  const filtersProps = {
    firstName: {
      type: 'input',
      name: 'first-name',
      id: 'f-name',
      label: t('page.ma.peopleAdd.firstName'),
      placeholder: t('forms.firstNamePlaceholder'),
    },
    lastName: {
      type: 'input',
      name: 'last-name',
      id: 'f-lastName',
      label: t('page.ma.peopleAdd.lastName'),
      placeholder: t('forms.lastNamePlaceholder'),
    },
    email: {
      type: 'input',
      name: 'email',
      id: 'f-email',
      label: t('page.ma.peopleAdd.email'),
      placeholder: t('forms.emailPlaceholder'),
    },
    createdFrom: {
      type: 'datepicker',
      name: 'createdFrom',
      id: 'f-createdFrom',
      label: t('page.ma.peopleAdd.createdFrom'),
      placeholder: t('forms.createdFromPlaceholder'),
    },
    membership: {
      type: 'select',
      name: 'membership',
      id: 'f-membership',
      label: t('page.ma.peopleAdd.status'),
      placeholder: t('forms.membershipPlaceholder'),
      options: [
        { value: 'Join', label: 'Join' },
        { value: 'Rejected', label: 'Rejected' },
      ],
    },
  };

  useEffect(() => {
    setPage(1);
  }, [perPage, filters]);

  useEffect(() => {
    loadUsers();
  }, [page, perPage, sort, filters]);

  const handleSortBy = (name) => () => {
    const [currentSortName, currentSortStatus] = sort || [];
    const sortByNewColumn = currentSortName !== name;

    if (sortByNewColumn) {
      setSort([name, 'desc']);
      return;
    }

    const newSortStatus = currentSortStatus === 'desc' ? 'asc' : 'desc';
    setSort([name, newSortStatus]);
  };

  const handleChangePerPage = ({ value }) => {
    setPerPage(value);
  };

  const getUserStatus = (user) => {
    if (!user.yCPeople) return 'No';
    return formatStatus(user.yCPeople.status);
  };

  const handleAction = (name, id) => () => {
    switch (name) {
      case 'accept':
        return acceptUser({ user: id });
      case 'decline':
        return declineUser({ user: id });
      default:
        return console.log('action not defined');
    }
  };

  const renderActions = (user) => {
    const status = user.yCPeople && user.yCPeople.status;
    return (
      <Row>
        <Col size={{ md: 2 / 5, xl: 1 / 2 }}>
          <ButtonAction
            onClick={() => setAcceptConfirmModal(user.id)}
            title={t('global.acceptButton')}
            icon="checkCircle"
          />
        </Col>
        {status === JOIN_STATUS ? (
          <Col size={{ md: 2 / 5, xl: 1 / 2 }}>
            <ButtonAction
              onClick={() => setDeclineConfirmModal(user.id)}
              title={t('global.declineButton')}
              icon="closeCircle"
            />
          </Col>
        ) : null}
      </Row>
    );
  };

  return (
    <>
      <Container>
        <PageContent>
          <Alerts type="yc" />
          <Margin top={{ xs: 16 }}>
            <FiltersCustom
              title={t('page.ma.peopleAdd.filterTitle')}
              filters={filtersProps}
              filterFields={filterFields}
              handleFilterFieldChange={handleFilterFieldChange}
              onFiltersApply={setFilters}
            />
          </Margin>
          <Margin top={{ xs: 16 }}>
            <RowStyled>
              <FiltersControls perPageOnChange={handleChangePerPage} perPageSelected={perPage} />
            </RowStyled>
          </Margin>
          <Table tablebreakpoint="lg">
            <Table.Head>
              <Table.Row>
                <Table.Heading sort={handleSortBy('firstLastName')}>
                  {t('page.yc.peopleList.firstAndLastName')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('email')}>
                  {t('page.yc.peopleList.email')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('createdAt')}>
                  {t('page.ma.peopleList.createdAt')}
                </Table.Heading>
                <Table.Heading sort={handleSortBy('status')}>
                  {t('page.yc.peopleList.membershipStatus')}
                </Table.Heading>
                <Table.Heading />
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <Spinner />
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && !items.length ? (
                <Table.Row>
                  <Table.Cell colSpan={4}>No results found</Table.Cell>
                </Table.Row>
              ) : null}
              {!loading && items.length
                ? items.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Table.Label>{t('page.yc.peopleList.firstAndLastName')}:</Table.Label>
                        <Link to={`/user-profile/${user.uniqueUrl}`}>
                          {`${user.firstName} ${user.lastName}`}
                        </Link>
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.yc.peopleList.email')}:</Table.Label>
                        {user.email}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.ma.peopleUsers.createdAt')}:</Table.Label>
                        {user.createdAt && moment(user.createdAt).format('DD/MM/YYYY')}
                      </Table.Cell>
                      <Table.Cell>
                        <Table.Label>{t('page.yc.peopleList.membershipStatus')}:</Table.Label>
                        {getUserStatus(user)}
                      </Table.Cell>
                      <Table.Cell>{renderActions(user)}</Table.Cell>
                    </Table.Row>
                  ))
                : null}
            </Table.Body>
          </Table>
          <Pagination styled currentPage={page} totalPages={pages} onChange={setPage} />
        </PageContent>
      </Container>
      <Modal
        heading={t('page.yc.peopleList.modalAcceptUser')}
        isOpen={!!acceptConfirmModal}
        confirm={() => handleAction('accept', acceptConfirmModal)() && setAcceptConfirmModal(null)}
        cancel={() => setAcceptConfirmModal(null)}
      />
      <Modal
        heading={t('page.yc.peopleList.modalDeclineUser')}
        isOpen={!!declineConfirmModal}
        confirm={() =>
          handleAction('decline', declineConfirmModal)() && setDeclineConfirmModal(null)
        }
        cancel={() => setDeclineConfirmModal(null)}
      />
    </>
  );
}

Applied.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  loadUsers: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  acceptUser: PropTypes.func.isRequired,
  declineUser: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  sort: PropTypes.arrayOf(PropTypes.string),
  filters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setFilters: PropTypes.func.isRequired,
};

Applied.defaultProps = {
  sort: [],
};

const mapStateToProps = ({ yc, loading }) => ({
  ...yc.people.applied,
  loading: !!loading.filter((item) => item.type === 'YC').length,
});

const mapDispatchToProps = (dispatch) => ({
  loadUsers: () => dispatch(loadYcPeopleAppliedCreator()),
  setSort: (payload) => dispatch(setYcPeopleAppliedSortCreator(payload)),
  setPage: (payload) => dispatch(setYcPeopleAppliedPageCreator(payload)),
  setPerPage: (payload) => dispatch(setYcPeopleAppliedPerPageCreator(payload)),
  acceptUser: (payload) => dispatch(acceptYcPeopleListUserCreator(payload)),
  declineUser: (payload) => dispatch(declineYcPeopleListUserCreator(payload)),
  setFilters: (payload) => dispatch(setYcPeopleAppliedFiltersCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Applied);
